import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Stack,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import Header from '../components/Layout/Header';
import Sidebar from '../components/Layout/Sidebar';
import { classService, periodService, subjectService, timetableService } from '../services/apiService';
import { showToast } from '../utils/toast';

const daysOfWeek = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const Routine = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const drawerWidth = 280;
  const headerBgColor = theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main;
  const headerTextColor = theme.palette.getContrastText(headerBgColor);

  // Convert 24-hour time to 12-hour format with AM/PM
  const formatTimeTo12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const filteredPeriods = useMemo(() => {
    return (periods || []).filter((period) => {
      const name = period?.period_name || '';
      return !/break/i.test(name);
    });
  }, [periods]);
  const filteredPeriodIds = useMemo(() => new Set(filteredPeriods.map((period) => period.id)), [filteredPeriods]);
  const [entriesMap, setEntriesMap] = useState(() => {
    const initial = {};
    daysOfWeek.forEach((day) => { initial[day.value] = {}; });
    return initial;
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [selectedDayTab, setSelectedDayTab] = useState(0);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const loadInitial = async () => {
      try {
        setLoading(true);
        setError('');
        const [classRes, periodRes] = await Promise.all([
          classService.getAll(),
          periodService.getAll(),
        ]);
        const classList = classRes.data || classRes || [];
        const periodList = (periodRes.data || periodRes || []).sort((a, b) => {
          const t1 = a.start_time || '';
          const t2 = b.start_time || '';
          return t1.localeCompare(t2);
        });
        setClasses(classList);
        setPeriods(periodList);
        // No default selection - user must select a class
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Failed to load initial data');
      } finally {
        setLoading(false);
      }
    };
    loadInitial();
  }, []);

  useEffect(() => {
    const loadClassData = async () => {
      if (!selectedClass) {
        setSubjects([]);
        setEntriesMap(() => {
          const initial = {};
          daysOfWeek.forEach((day) => { initial[day.value] = {}; });
          return initial;
        });
        return;
      }
      try {
        setLoading(true);
        const [subjectsRes, timetableRes] = await Promise.all([
          subjectService.getAll({ class_id: selectedClass }),
          timetableService.getAll({ class_id: selectedClass }),
        ]);
        setSubjects(subjectsRes.data || subjectsRes || []);
        const data = timetableRes.data || timetableRes || [];
        const map = {};
        const allowedPeriodIds = new Set((periods || []).filter((period) => {
          const name = period?.period_name || '';
          return !/break/i.test(name);
        }).map((period) => period.id));
        daysOfWeek.forEach((day) => { map[day.value] = {}; });
        data.forEach((entry) => {
          if (entry.period_id && entry.day_of_week && allowedPeriodIds.has(entry.period_id)) {
            map[entry.day_of_week][entry.period_id] = entry.subject_id;
          }
        });
        setEntriesMap(map);
        setEditing(false);
      } catch (err) {
        showToast.error(err?.response?.data?.error || err?.message || 'Failed to load routine data');
      } finally {
        setLoading(false);
      }
    };
    loadClassData();
  }, [selectedClass, periods]);

  const handleSubjectChange = (day, periodId) => (event) => {
    const value = event.target.value;
    setEntriesMap((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [periodId]: value,
      },
    }));
  };

  const handleClearPeriod = (day, periodId) => {
    setEntriesMap((prev) => ({
      ...prev,
      [day]: Object.keys(prev[day] || {}).reduce((acc, key) => {
        if (key !== periodId) acc[key] = prev[day][key];
        return acc;
      }, {}),
    }));
  };

  const handleClearAll = () => {
    setEntriesMap(() => {
      const initial = {};
      daysOfWeek.forEach((day) => { initial[day.value] = {}; });
      return initial;
    });
  };

  const handleSave = async () => {
    if (!selectedClass || !editing) {
      if (!selectedClass) {
        showToast.error('Please select a class');
      }
      return;
    }
    try {
      setSaving(true);
      for (const day of daysOfWeek) {
        const dayEntries = entriesMap[day.value] || {};
        const payloadEntries = Object.entries(dayEntries)
          .filter(([periodId, subjectId]) => subjectId && filteredPeriodIds.has(periodId))
          .map(([periodId, subjectId]) => ({ period_id: periodId, subject_id: subjectId }));
        await timetableService.saveDay({
          class_id: selectedClass,
          day_of_week: day.value,
          entries: payloadEntries,
        });
      }
      showToast.success('Routine saved successfully');
      setEditing(false);
    } catch (err) {
      showToast.error(err?.response?.data?.error || err?.message || 'Failed to save routine');
    } finally {
      setSaving(false);
    }
  };

  const classOptions = useMemo(() => {
    return (classes || []).map((cls) => ({
      value: cls.id,
      label: `${cls.name || 'Class'}${cls.section ? ` - ${cls.section}` : ''}`,
    }));
  }, [classes]);

  const subjectOptions = useMemo(() => {
    return (subjects || []).map((subj) => ({
      value: subj.id,
      label: subj.name,
      teacher: subj.teacher_name,
    }));
  }, [subjects]);

  const subjectById = useMemo(() => {
    const map = new Map();
    subjectOptions.forEach((subj) => {
      map.set(subj.value, subj);
    });
    return map;
  }, [subjectOptions]);

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              p: { xs: 1.5, sm: 2, md: 3 },
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
              width: '100%',
              maxWidth: '100%',
              overflowX: 'hidden',
              boxSizing: 'border-box',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, sm: 2, md: 3 },
                borderRadius: 3,
                border: 1,
                borderColor: 'divider',
                mb: 3,
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            >
              <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                Routine Management
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { xs: 'stretch', md: 'center' } }}>
                <Autocomplete
                  options={classOptions}
                  getOptionLabel={(option) => option ? `Class ${option.label}` : ''}
                  value={classOptions.find((cls) => cls.value === selectedClass) || null}
                  onChange={(event, newValue) => {
                    setSelectedClass(newValue ? newValue.value : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Class"
                      placeholder="Search and select class..."
                      size="small"
                    />
                  )}
                  sx={{ flex: { xs: '1 1 100%', md: '0 1 auto' }, minWidth: { xs: '100%', md: 360 } }}
                  ListboxProps={{
                    style: {
                      maxHeight: 200,
                    },
                  }}
                  noOptionsText="No classes found"
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: { xs: 1, sm: 1.5 },
                    ml: { md: 'auto' },
                    flexDirection: { xs: 'column', sm: 'row-reverse' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || loading || !selectedClass || !editing}
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
                    fullWidth={isMobile}
                    size={isMobile ? 'medium' : 'medium'}
                  >
                    {saving ? 'Saving...' : isMobile ? 'Save' : 'Save Routine'}
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={saving || loading || !editing}
                    onClick={handleClearAll}
                    fullWidth={isMobile}
                    size={isMobile ? 'medium' : 'medium'}
                  >
                    Clear All
                  </Button>
                  <Button
                    variant={editing ? 'contained' : 'outlined'}
                    color={editing ? 'secondary' : 'primary'}
                    onClick={() => setEditing((prev) => !prev)}
                    disabled={loading}
                    fullWidth={isMobile}
                    size={isMobile ? 'medium' : 'medium'}
                  >
                    {editing ? (isMobile ? 'Exit Edit' : 'Exit Edit Mode') : (isMobile ? 'Edit' : 'Enable Edit Mode')}
                  </Button>
                </Box>
              </Box>
            </Paper>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, sm: 2, md: 3 },
                borderRadius: 3,
                border: 1,
                borderColor: 'divider',
                overflow: 'hidden',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}
            >
              {!selectedClass ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    Please select a class to view the routine
                  </Typography>
                </Box>
              ) : loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : filteredPeriods.length === 0 ? (
                <Alert severity="info">No instructional periods configured (break periods are hidden). Please add periods in Setup &gt; Class Periods.</Alert>
              ) : (
                <>
                  {/* Tabs for weekdays */}
                  <Box
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      mb: { xs: 2, md: 3 },
                      width: '100%',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Tabs
                      value={selectedDayTab}
                      onChange={(e, newValue) => setSelectedDayTab(newValue)}
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      sx={{
                        width: '100%',
                        maxWidth: '100%',
                        overflowX: 'auto',
                        '& .MuiTabs-scroller': {
                          overflowX: 'auto !important',
                          scrollbarWidth: 'thin',
                        },
                        '& .MuiTab-root': {
                          minWidth: { xs: 60, sm: 80, md: 100 },
                          maxWidth: { xs: 80, sm: 120, md: 'none' },
                          fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                          px: { xs: 1, sm: 1.5, md: 2 },
                          textTransform: 'none',
                          fontWeight: 600,
                          flexShrink: 0,
                        },
                        '& .MuiTabs-scrollButtons': {
                          width: { xs: 32, sm: 40 },
                          flexShrink: 0,
                        },
                        '& .MuiTabs-indicator': {
                          display: 'block',
                        },
                      }}
                    >
                      {daysOfWeek.map((day, index) => (
                        <Tab
                          key={day.value}
                          label={isMobile ? day.label.substring(0, 3) : day.label}
                          sx={{
                            minHeight: { xs: 48, sm: 56 },
                          }}
                        />
                      ))}
                    </Tabs>
                  </Box>

                  {/* Routine content for selected day */}
                  <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
                    {daysOfWeek.map((day, index) => (
                      <Box
                        key={day.value}
                        role="tabpanel"
                        hidden={selectedDayTab !== index}
                        id={`routine-tabpanel-${index}`}
                        aria-labelledby={`routine-tab-${index}`}
                        sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
                      >
                        {selectedDayTab === index && (
                            <TableContainer
                              sx={{
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                width: '100%',
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                                overflowX: 'auto',
                              }}
                            >
                              <Table sx={{ minWidth: 300 }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                      Period
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                      Time
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                      Subject
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                      Teacher
                                    </TableCell>
                                    {editing && (
                                      <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.875rem' }, width: 80 }}>
                                        Action
                                      </TableCell>
                                    )}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {filteredPeriods.map((period) => {
                                    const selectedSubject = entriesMap[day.value]?.[period.id] || '';
                                    const subjectDetails = subjectById.get(selectedSubject);
                                    return (
                                      <TableRow
                                        key={period.id}
                                        sx={{
                                          backgroundColor: selectedSubject ? 'action.hover' : 'transparent',
                                          '&:hover': {
                                            backgroundColor: selectedSubject ? 'action.selected' : 'action.hover',
                                          },
                                        }}
                                      >
                                        <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                          {period.period_name || 'Period'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                          {formatTimeTo12Hour(period.start_time)} - {formatTimeTo12Hour(period.end_time)}
                                        </TableCell>
                                        <TableCell>
                                          {editing ? (
                                            <FormControl
                                              fullWidth
                                              size="small"
                                              sx={{
                                                minWidth: 150,
                                              }}
                                            >
                                              <Select
                                                value={selectedSubject}
                                                onChange={handleSubjectChange(day.value, period.id)}
                                                displayEmpty
                                                sx={{
                                                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                }}
                                              >
                                                <MenuItem value="">
                                                  <em>Unassigned</em>
                                                </MenuItem>
                                                {subjectOptions.map((subj) => (
                                                  <MenuItem
                                                    key={subj.value}
                                                    value={subj.value}
                                                    sx={{
                                                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                    }}
                                                  >
                                                    {subj.label}
                                                    {subj.teacher ? ` — ${subj.teacher}` : ''}
                                                  </MenuItem>
                                                ))}
                                              </Select>
                                            </FormControl>
                                          ) : (
                                            <Box>
                                              {subjectDetails ? (
                                                <>
                                                  <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    sx={{
                                                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                      mb: subjectDetails.teacher ? 0.25 : 0,
                                                    }}
                                                  >
                                                    {subjectDetails.label}
                                                  </Typography>
                                                </>
                                              ) : (
                                                <Typography
                                                  variant="body2"
                                                  color="text.secondary"
                                                  fontStyle="italic"
                                                  sx={{
                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                  }}
                                                >
                                                  Unassigned
                                                </Typography>
                                              )}
                                            </Box>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {subjectDetails?.teacher ? (
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                color: 'text.secondary',
                                              }}
                                            >
                                              {subjectDetails.teacher}
                                            </Typography>
                                          ) : (
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                              fontStyle="italic"
                                              sx={{
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                              }}
                                            >
                                              —
                                            </Typography>
                                          )}
                                        </TableCell>
                                        {editing && (
                                          <TableCell>
                                            {selectedSubject && (
                                              <Button
                                                size="small"
                                                color="error"
                                                onClick={() => handleClearPeriod(day.value, period.id)}
                                                sx={{
                                                  minWidth: 'auto',
                                                  p: 0.5,
                                                }}
                                                aria-label="Clear period"
                                              >
                                                <DeleteIcon fontSize="small" />
                                              </Button>
                                            )}
                                          </TableCell>
                                        )}
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                        )}
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Routine;
